import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { Media } from '@/js/libs/ui';
import { Button, Icons, Link, Tooltip, DropdownList } from '@/js/libs/ui/src/V2';
import { PrivateContent, Roles, useIdentityRoles } from '@/js/libs/use-auth';

import { ROUTER } from 'src/AppRouter';
import { DEV_MODE } from 'src/constants';
import { useThemeController } from 'src/theme/ThemeController';
import { useNavigationContext } from '../../context';
import { LogoLarge, LogoSmall } from './Logo';
import {
  DevBadge,
  ErrorIndicator,
  LogoMenuGroup,
  MailIconIndicator,
  MailIconWrapper,
  ToggleButton,
  ToggleButtonWrapper,
  Wrapper,
} from './styled';

type Props = {
  onNeedSearchAutofocus?: (needAutoFocus: boolean) => void;
};

export const Header = memo(function Header({ onNeedSearchAutofocus }: Props) {
  const [t] = useTranslation(['backend', 'common']);
  const { isSwitching, checkTheme, setTheme } = useThemeController();
  const { colors } = useTheme();
  const isDesktop = useMediaQuery({ query: Media.lgMin });
  const { SWITCH_COMPANY_VIEW, BOOK_CAMPAIGNS, AGENCY, POSTADMANAGER, POSTNETZ_BRANCH_USER, SELF_BOOKING } = useIdentityRoles();

  const { collapseMainNav, isCollapsedNav, unreadCommentsCountRequest, pendingUnreadCommentsCount } = useNavigationContext();

  const unreadCommentsCount = useSubscribe(unreadCommentsCountRequest.data$)?.data ?? 0;
  const unreadCommentsCountError = useSubscribe(unreadCommentsCountRequest.error$);

  const onMessagesClick = useCallbackRef(() => ROUTER['/orders/comments'].path.navigate());

  const onSearchClick = useCallbackRef(() => {
    onNeedSearchAutofocus?.(true);
    collapseMainNav();
  });

  return (
    <Wrapper isCollapsedNav={isCollapsedNav} isCompanySwitchView={!!SWITCH_COMPANY_VIEW}>
      <LogoMenuGroup>
        <ToggleButtonWrapper isCollapsedNav={isCollapsedNav}>
          {DEV_MODE && <DevBadge xs warning title={t('common:DEV_BADGE_TITLE')} isCollapsedNav={isCollapsedNav} />}
          <ToggleButton onClick={collapseMainNav} isCollapsedNav={isCollapsedNav}>
            <Icons.Menu size={24} active={isCollapsedNav} />
            {isCollapsedNav && isDesktop && <LogoSmall />}
          </ToggleButton>
          {(!isCollapsedNav || !isDesktop) && (
            <>
              {BOOK_CAMPAIGNS || AGENCY || POSTADMANAGER ? (
                <Link to='/' tabIndex={-1}>
                  <LogoLarge />
                </Link>
              ) : (
                <LogoLarge />
              )}
            </>
          )}
        </ToggleButtonWrapper>
      </LogoMenuGroup>

      <PrivateContent roles={Roles.ADMIN}>
        <Button iconOnly ghost onClick={onMessagesClick} dataTestId='mail-button'>
          {unreadCommentsCountError ? (
            <Tooltip ContainerComponent={MailIconWrapper} tooltip={t('common:MESSAGES_SERVICE_UNAVAILABLE')}>
              <ErrorIndicator />
              <Icons.Mail color={colors.grey100_1} />
            </Tooltip>
          ) : (
            <Tooltip
              ContainerComponent={MailIconWrapper}
              tooltip={
                !pendingUnreadCommentsCount &&
                unreadCommentsCount > 0 &&
                t('common:UNREAD_MESSAGES_COUNT_TOOLTIP', { count: unreadCommentsCount })
              }>
              {!pendingUnreadCommentsCount && unreadCommentsCount > 0 && <MailIconIndicator />}
              <Icons.Mail color={colors.grey100_1} />
            </Tooltip>
          )}
        </Button>
      </PrivateContent>

      {isDesktop && isCollapsedNav && !POSTNETZ_BRANCH_USER && !POSTADMANAGER && !SELF_BOOKING && (
        <Button iconOnly ghost onClick={onSearchClick}>
          <Icons.Search color={colors.grey100_1} />
        </Button>
      )}

      {/* ⬇︎⬇︎⬇︎ Temporary solution until backend is ready ⬇︎⬇︎⬇︎ */}
      <DropdownList
        minWidth={false}
        buttonComponent={
          <Button iconOnly ghost isLoading={isSwitching} data-testid='ui-mode-select'>
            <Icons.Gear color={colors.grey100_1} />
          </Button>
        }>
        <DropdownList.DropdownItem
          icon={<Icons.Flash color={checkTheme('legacyOrange') ? colors.main : colors.grey100_1} />}
          onClick={() => setTheme('legacyOrange')}
          dataTestId='legacy-orange-mode'>
          {t('common:DEFAULT')}
        </DropdownList.DropdownItem>
        <DropdownList.DropdownItem
          icon={<Icons.Flash color={checkTheme('primary') ? colors.main : colors.grey100_1} />}
          onClick={() => setTheme('primary')}
          dataTestId='primary-mode'>
          {t('common:THEME_PRIMARY')}
        </DropdownList.DropdownItem>
        <DropdownList.DropdownItem
          icon={<Icons.Flash color={checkTheme('primaryDark') ? colors.main : colors.grey100_1} />}
          onClick={() => setTheme('primaryDark')}
          dataTestId='primary-dark-mode'>
          {t('common:THEME_PRIMARY_DARK')}
        </DropdownList.DropdownItem>
        <DropdownList.DropdownItem
          icon={<Icons.Flash color={checkTheme('system') ? colors.main : colors.grey100_1} />}
          onClick={() => setTheme('system')}>
          {t('common:THEME_SYSTEM')}
        </DropdownList.DropdownItem>
      </DropdownList>
      {/* ⬆︎⬆︎⬆︎ Temporary solution until backend is ready ⬆︎⬆︎⬆︎ */}
    </Wrapper>
  );
});
